/**
 * TSM Groups Configuration Tab
 *
 * Lets Admins assign each Service Advisor (TSM) at each location to one of
 * three groups: Domestic, Import, or Express. The assignments drive the
 * TSM Group Ranking Report (Reports → Service tab).
 *
 * UX:
 *  - One collapsible section per service-bearing location (1, 2, 3, 4, 5,
 *    10, 11, 14). Loc 12 & 44 are Body Shop and not part of the report.
 *  - Each row in an expanded section is an advisor + a group dropdown
 *    (Unassigned / Domestic / Import / Express).
 *  - Save-per-change: picking a group fires a PUT immediately. Picking
 *    "Unassigned" fires a DELETE. Inline "Saved ✓" / "Failed" indicator.
 *
 * Advisor list source: the current month's service-advisor-performance
 * payload for each of the 8 locations, fetched in parallel on mount.
 * The advisor identifier we save (`advisorId`) is the DealerBuilt employee
 * number — stable across months and locations.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import {
  getServiceAdvisorPerformance,
  saveTsmGroupAssignment,
  deleteTsmGroupAssignment,
  TsmGroup,
  TSM_GROUPS,
  TsmGroupAssignmentConfig,
  LocationScheduleConfig,
} from '../../services/api';

// Service-bearing locations (matches TSM Ranking Report — Loc 12 & 44 are
// Body Shop and excluded).
const SERVICE_LOCATION_IDS = [1, 2, 3, 4, 5, 10, 11, 14];

interface AdvisorRow {
  advisorId: string;
  advisorName: string;
  roCountTotal: number;
  openROCount: number;
}

type RowStatus = 'idle' | 'saving' | 'saved' | 'error';

interface TsmGroupsTabProps {
  canEdit: boolean;
  locationSchedules: LocationScheduleConfig[];
  tsmGroupAssignments: TsmGroupAssignmentConfig[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  refresh: () => Promise<void>;
}

export const TsmGroupsTab: React.FC<TsmGroupsTabProps> = ({
  canEdit,
  locationSchedules,
  tsmGroupAssignments,
  onSuccess,
  onError,
  refresh,
}) => {
  const [loading, setLoading] = useState(true);
  const [advisorsByLocation, setAdvisorsByLocation] = useState<Map<number, AdvisorRow[]>>(new Map());
  const [expandedLocation, setExpandedLocation] = useState<number | null>(null);
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});

  const now = useMemo(() => new Date(), []);
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  // Fetch current-month advisor lists for all 8 service locations in parallel.
  const fetchAdvisors = useCallback(async () => {
    setLoading(true);
    const results = await Promise.all(
      SERVICE_LOCATION_IDS.map(async (locationId) => {
        const response = await getServiceAdvisorPerformance(locationId, year, month);
        if (response.success && response.data) {
          const rows: AdvisorRow[] = response.data.advisors.map((a) => ({
            advisorId: a.advisorId,
            advisorName: a.advisorName,
            roCountTotal: a.roCountTotal,
            openROCount: a.openROCount ?? 0,
          }));
          // Only show advisors with activity this month — drops formal employees
          // who haven't written ROs in the current period (no point assigning a
          // group to someone who won't appear in the report).
          rows.sort((a, b) => a.advisorName.localeCompare(b.advisorName));
          return [locationId, rows] as const;
        }
        return [locationId, [] as AdvisorRow[]] as const;
      }),
    );
    const map = new Map<number, AdvisorRow[]>();
    for (const [locationId, rows] of results) {
      map.set(locationId, rows);
    }
    setAdvisorsByLocation(map);
    setLoading(false);
  }, [year, month]);

  useEffect(() => {
    fetchAdvisors();
  }, [fetchAdvisors]);

  // Index assignments by `${locationId}_${advisorId}` for O(1) lookups.
  const assignmentIndex = useMemo(() => {
    const map = new Map<string, TsmGroup>();
    for (const a of tsmGroupAssignments) {
      map.set(`${a.locationId}_${a.advisorId}`, a.group);
    }
    return map;
  }, [tsmGroupAssignments]);

  const flashStatus = (key: string, status: RowStatus) => {
    setRowStatus((prev) => ({ ...prev, [key]: status }));
    if (status === 'saved' || status === 'error') {
      setTimeout(() => {
        setRowStatus((prev) => {
          if (prev[key] !== status) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }, 2500);
    }
  };

  const handleGroupChange = async (
    locationId: number,
    advisor: AdvisorRow,
    newValue: TsmGroup | '',
  ) => {
    if (!canEdit) return;
    const key = `${locationId}_${advisor.advisorId}`;
    flashStatus(key, 'saving');

    if (newValue === '') {
      // "Unassigned" — delete the assignment (if any).
      const existing = assignmentIndex.get(key);
      if (!existing) {
        // Nothing to delete; just refresh to be safe.
        flashStatus(key, 'saved');
        return;
      }
      const response = await deleteTsmGroupAssignment(locationId, advisor.advisorId);
      if (response.success) {
        await refresh();
        flashStatus(key, 'saved');
        onSuccess(`Cleared group for ${advisor.advisorName}`);
      } else {
        flashStatus(key, 'error');
        onError(response.error || 'Failed to clear assignment');
      }
      return;
    }

    const response = await saveTsmGroupAssignment({
      type: 'TSM_GROUP_ASSIGNMENT',
      locationId,
      advisorId: advisor.advisorId,
      advisorName: advisor.advisorName,
      group: newValue,
    });
    if (response.success) {
      await refresh();
      flashStatus(key, 'saved');
      onSuccess(`${advisor.advisorName} → ${newValue}`);
    } else {
      flashStatus(key, 'error');
      onError(response.error || 'Failed to save assignment');
    }
  };

  // Filter location schedules to the 8 service locations and preserve their order.
  const visibleLocations = useMemo(() => {
    return SERVICE_LOCATION_IDS
      .map((id) => locationSchedules.find((l) => l.locationId === id))
      .filter((l): l is LocationScheduleConfig => Boolean(l));
  }, [locationSchedules]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading Service Advisors...</span>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Assign each Service Advisor to a group — <strong>Domestic</strong>, <strong>Import</strong>, or
        {' '}<strong>Express</strong> — to drive the TSM Group Ranking Report (Reports → Service).
        Advisors without a group fall into the "Unassigned" section of the report.
        The list shows advisors with activity in the current month
        ({now.toLocaleString('en-US', { month: 'long' })} {year}).
      </p>

      <div className="space-y-4">
        {visibleLocations.map((location) => {
          const advisors = advisorsByLocation.get(location.locationId) ?? [];
          const isExpanded = expandedLocation === location.locationId;
          const assignedCount = advisors.filter(
            (a) => assignmentIndex.has(`${location.locationId}_${a.advisorId}`),
          ).length;

          return (
            <div key={location.locationId} className="border border-gray-200 rounded-lg">
              <button
                onClick={() => setExpandedLocation(isExpanded ? null : location.locationId)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t-lg"
              >
                <div className="flex items-center space-x-3">
                  <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                    ▶
                  </span>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">{location.locationName}</div>
                    <div className="text-sm text-gray-500">
                      {advisors.length === 0
                        ? 'No advisors with activity this month'
                        : `${assignedCount} of ${advisors.length} advisors assigned`}
                    </div>
                  </div>
                </div>
                {assignedCount === advisors.length && advisors.length > 0 && (
                  <span className="inline-flex items-center text-xs text-green-700 bg-green-50 ring-1 ring-green-200 px-2 py-0.5 rounded">
                    <CheckIcon className="h-3 w-3 mr-1" />
                    Complete
                  </span>
                )}
              </button>

              {isExpanded && (
                <div className="p-4">
                  {advisors.length === 0 ? (
                    <div className="text-sm text-gray-500 italic py-4 text-center">
                      No Service Advisors with activity at this location for the current month.
                    </div>
                  ) : (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-left">
                          <th className="px-2 py-2 font-semibold text-gray-700">Advisor</th>
                          <th className="px-2 py-2 font-semibold text-gray-700 text-right">
                            ROs (MTD)
                          </th>
                          <th className="px-2 py-2 font-semibold text-gray-700 text-right">
                            Open ROs
                          </th>
                          <th className="px-2 py-2 font-semibold text-gray-700">Group</th>
                          <th className="px-2 py-2 w-20"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {advisors.map((advisor) => {
                          const key = `${location.locationId}_${advisor.advisorId}`;
                          const currentGroup = assignmentIndex.get(key) ?? '';
                          const status = rowStatus[key];
                          return (
                            <tr key={advisor.advisorId} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-2 py-2 font-medium text-gray-900">
                                {advisor.advisorName}
                                <span className="ml-2 text-xs text-gray-400 font-mono">
                                  #{advisor.advisorId}
                                </span>
                              </td>
                              <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                                {advisor.roCountTotal.toLocaleString()}
                              </td>
                              <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                                {advisor.openROCount.toLocaleString()}
                              </td>
                              <td className="px-2 py-2">
                                <select
                                  value={currentGroup}
                                  onChange={(e) =>
                                    handleGroupChange(
                                      location.locationId,
                                      advisor,
                                      e.target.value as TsmGroup | '',
                                    )
                                  }
                                  disabled={!canEdit || status === 'saving'}
                                  className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm disabled:opacity-60"
                                >
                                  <option value="">— Unassigned —</option>
                                  {TSM_GROUPS.map((g) => (
                                    <option key={g} value={g}>{g}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2 text-xs">
                                {status === 'saving' && (
                                  <span className="text-gray-500">Saving…</span>
                                )}
                                {status === 'saved' && (
                                  <span className="text-green-700 font-medium">Saved ✓</span>
                                )}
                                {status === 'error' && (
                                  <span className="text-rose-700 font-medium">Failed</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {visibleLocations.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No service locations available. Run the location-schedule seed first.
        </div>
      )}
    </div>
  );
};
